import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DID_CLASS, DID_TYPE, AddPortDidFormEmittedValue } from 'voxloud-types/dist/models';
import { DidsService } from 'src/app/domains/dids/services';
import { VerifyPhoneNumberService } from 'src/app/shared/services/verify-phone-number/verify-phone-number.service';
import { checkPhoneNumberExistsAsync } from 'src/app/shared/validators/check-phone-number-exists.async.validator';

@Component({
  selector: 'gdp-add-port-did-form',
  templateUrl: './add-port-did-form.component.html',
  styleUrls: ['./add-port-did-form.component.scss']
})
export class AddPortDidFormComponent implements OnInit {
  public companyId: number;
  public form: UntypedFormGroup;
  public availableClasses = Object.keys(DID_CLASS);
  public availableTypes = Object.keys(DID_TYPE);
  private formSubscription: Subscription;
  @Input() availableProviders: Array<string> = [];
  @Input() country: string;
  @Output() onChangeCountry: EventEmitter<string> = new EventEmitter();
  @Output() onEmitFormValues: EventEmitter<{ value: AddPortDidFormEmittedValue, isValid: boolean }> = new EventEmitter();

  constructor(
    private fb: UntypedFormBuilder,
    private verifyPhoneService: VerifyPhoneNumberService,
    private didsService: DidsService
    ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      class: [this.getStoredFormValue?.class || DID_CLASS.VOICE, [Validators.required]],
      type: [this.getStoredFormValue?.type || DID_TYPE.GEOGRAPHIC, [Validators.required]],
      phone: [this.getStoredFormValue?.phone || null, [Validators.required],
      checkPhoneNumberExistsAsync(this.verifyPhoneService)],
      provider: [this.getStoredFormValue?.provider || this.availableProviders[0]],
      providerReference: [this.getStoredFormValue?.providerReference || null],
      couponId: [this.getStoredFormValue?.couponId || null]
    });

    this.formSubscription = this.form.get('provider').valueChanges.subscribe((v) => {
      this.validateAndEmit();
    })
  }

  get getStoredFormValue() {
    return this.didsService.getAddPortDidFormValue();
  }

  isFieldInvalid(name: string) {
    return (
      !this.form.get(name).valid && this.form.get(name).touched
    );
  }

  // Methods for phone number validation
  isPhoneNumberTaken(): boolean {
    const phoneControl = this.form.controls.phone;
    return (
      phoneControl.errors &&
      phoneControl.errors.phoneNumberExists
    );
  }

  getAsyncPendingStatus(): boolean {
    return this.form.controls.phone.pending;
  }

  isPrefixBlocked(): boolean {
    const phoneControl = this.form.controls.phone;
    return (
      phoneControl.errors &&
      phoneControl.errors.blocked_prefix
    );
  }

  getPhoneNumberToken(): string {
    const phoneControl = this.form.controls.phone;
    if (!phoneControl.value) {
      return "company.dids.add-port-did.popup.form.required";
    } else if (this.isPhoneNumberTaken()) {
      return "company.dids.add-port-did.popup.form.phone.errors.taken";
    } else if (this.isPrefixBlocked()) {
      return "company.dids.add-port-did.popup.form.phone.errors.prefix";
    } else {
      return "company.dids.add-port-did.popup.form.phone.errors.invalid";
    }
  }

  checkValidationAfterApiError(): void {
    const phoneControl = this.form.controls.phone;
    phoneControl.updateValueAndValidity();
  }

  selectDidClass(did_class: DID_CLASS) {
    this.form.get('class').setValue(did_class);
    this.validateAndEmit();
  }

  selectDidType(did_type: DID_TYPE) {
    this.form.get('type').setValue(did_type);
    this.validateAndEmit();
  }

  onChangeInputValue() {
    this.validateAndEmit();
  }

  onChangeState(s: any) {
    this.country = s.iso2;
    this.onChangeCountry.emit(this.country);
    this.validateAndEmit();
  }

  private validateAndEmit(): void {
    const phoneControl = this.form.controls.phone;
    const formResult = {
      value: { ...this.form.value, provider: this.form.controls.provider.value},
      isValid: !phoneControl.errors ? true : this.form.valid
    };
    this.didsService.setAddPortDidFormValue(formResult.value);
    this.onEmitFormValues.emit(formResult);
  }

  public ngOnDestroy() {
    this.formSubscription?.unsubscribe();
  }
}
